import * as THREE from "three";

// Full-canvas interaction helpers (panning/selection/construction/fill planes) all tag their
// mesh with a "*-plane" userData id, whereas real objects (walls/lines/symbols/handles) use
// 9-char base36 ids that never contain a hyphen. Matching the "-plane" suffix therefore
// reliably tells a background element apart from an actual object the user wants to select.
export const objectIsHelperPlane = (object: THREE.Object3D): boolean => {
  let current: THREE.Object3D | null = object;
  while (current) {
    const id = current.userData?.id;
    if (typeof id === "string" && id.endsWith("-plane")) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

// Walk an object's ancestor chain to decide whether it belongs to `ancestor`'s subtree. Used
// to recognise a background element's own meshes (fill/interaction/handles) inside a pointer
// event's intersection list.
export const objectIsDescendantOf = (
  object: THREE.Object3D,
  ancestor: THREE.Object3D | null,
): boolean => {
  if (!ancestor) {
    return false;
  }
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current === ancestor) {
      return true;
    }
    current = current.parent;
  }
  return false;
};

// Decide whether a real, selectable object sits on top of `ownObject` for a given pointer
// event. R3F sorts intersections near→far, so the first hit that is neither a helper plane
// nor part of `ownObject` is whatever the user actually sees on top. Background elements (an
// area fill, a backdrop) call this before claiming a click so they yield to the element on
// top instead of greedily stealing the selection - the situation that otherwise makes symbols
// unselectable while panning, where the background's drag is disabled but its click is not.
export const pointerEventHasObjectInFront = (
  intersections: THREE.Intersection[] | undefined,
  ownObject: THREE.Object3D | null,
): boolean => {
  if (!intersections || intersections.length === 0 || !ownObject) {
    return false;
  }

  for (const hit of intersections) {
    if (objectIsHelperPlane(hit.object)) {
      continue;
    }
    // Nearest real interactive object decides the click: yield only when it is something
    // other than the background element itself.
    return !objectIsDescendantOf(hit.object, ownObject);
  }

  return false;
};
